import { createContext, useContext, useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useNoIndex } from "@/lib/seo";

const AdminContext = createContext(null);

export function AdminProvider({ children }) {
  const [user, setUser] = useState(null);
  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);
  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("pmg_token", data.token);
    setUser(data.user);
  };
  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    localStorage.removeItem("pmg_token");
    setUser(false);
  };
  return <AdminContext.Provider value={{ user, login, logout }}>{children}</AdminContext.Provider>;
}

export const useAdmin = () => useContext(AdminContext);

export const RequireAdmin = ({ children }) => {
  const { user } = useAdmin();
  const loc = useLocation();
  if (user === null) return <div className="admin flex items-center justify-center"><span className="mono text-[#8b95a5]">…</span></div>;
  if (!user) return <Navigate to="/admin/login" state={{ from: loc.pathname }} replace />;
  return children;
};

const Shell = ({ title, children }) => {
  useNoIndex();
  return (
  <div className="admin flex items-center justify-center px-6">
    <div className="w-full max-w-sm">
      <p className="display text-xl">Palissiere</p>
      <p className="mono mt-1 text-[0.6rem] text-[#8b95a5]">Motor Group · Admin</p>
      <h1 className="display-tight mt-10 text-2xl">{title}</h1>
      <div className="mt-8">{children}</div>
    </div>
  </div>
  );
};

export function AdminLogin() {
  const { user, login } = useAdmin();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to={loc.state?.from || "/admin"} replace />;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      nav(loc.state?.from || "/admin", { replace: true });
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell title="Sign in">
      <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
        <input className="field" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="admin-login-email" />
        <input className="field" type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="admin-login-password" />
        <button type="submit" disabled={busy} className="btn btn-primary w-full disabled:opacity-60" data-testid="admin-login-submit">{busy ? "…" : "SIGN IN"}</button>
        <Link to="/admin/forgot-password" className="mono block text-center text-[0.6rem] text-[#8b95a5] hover:text-white" data-testid="admin-forgot-link">Forgot password?</Link>
      </form>
    </Shell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };
  return (
    <Shell title="Reset password">
      {sent ? (
        <p className="text-sm text-[#c7ccd4]" data-testid="forgot-success">If this account exists, a reset link has been sent.</p>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
          <input className="field" type="email" placeholder="Admin email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="forgot-email" />
          <button className="btn btn-primary w-full" data-testid="forgot-submit">SEND RESET LINK</button>
          <Link to="/admin/login" className="mono block text-center text-[0.6rem] text-[#8b95a5]">Back to sign in</Link>
        </form>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/reset-password", { token: params.get("token"), password });
      toast.success("Password updated");
      nav("/admin/login");
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };
  return (
    <Shell title="New password">
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        <input className="field" type="password" minLength={8} placeholder="New password (8+ characters)" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="reset-password" />
        <button className="btn btn-primary w-full" data-testid="reset-submit">UPDATE PASSWORD</button>
      </form>
    </Shell>
  );
}
